import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

// 색상은 모두 CSS 변수(--aichat-*)로 토큰화 — 값은 themes.ts(light/dark)에서 Root 에 주입.

// 스피너 → 콘텐츠 전환(및 패널 열림) 부드럽게 — 최초 노출 시 1회 페이드인
const contentIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

export const Root = styled.div<{ isMobile?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ isMobile }) =>
    isMobile ? 'var(--aichat-bg-mobile)' : 'var(--aichat-bg)'};
  overflow: hidden;
`;

// PC: 좌 타이틀 / 우 [채팅 초기화 | 닫기]
export const Header = styled.div`
  min-height: 48px;
  padding: 12px 20px 12px 8px;
  border-bottom: 1px solid var(--aichat-border);
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  background: var(--aichat-bg);
  backdrop-filter: blur(8px);
`;

export const TitleWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

// 채팅 초기화와 닫기 사이 세로 구분선
export const HeaderDivider = styled.div`
  width: 1px;
  height: 16px;
  flex-shrink: 0;
  background: var(--aichat-header-divider);
`;

export const TitleIcon = styled.div`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`;

export const Title = styled.span`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: var(--aichat-text);
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background: var(--aichat-hover);
  }
`;

// 채팅 초기화 — 헤더 텍스트 버튼
export const ResetButton = styled.button`
  margin-left: auto;
  padding: 0 2px;
  border: none;
  border-radius: 4px;
  background: transparent;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: var(--aichat-link);
  text-decoration: underline;
  text-underline-position: from-font;
  white-space: nowrap;
  cursor: pointer;

  &:disabled {
    cursor: default;
    color: var(--aichat-link-disabled);
  }
`;

// 모바일 헤더: 왼쪽 뒤로가기 버튼 + 가운데 정렬 타이틀
export const MobileHeader = styled.div`
  position: relative;
  min-height: 48px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--aichat-border);
  display: flex;
  align-items: center;
  flex-shrink: 0;
  background: var(--aichat-bg-mobile);
`;

export const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  margin-left: -4px;
  border: none;
  background: transparent;
  cursor: pointer;
`;

export const MobileTitle = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: var(--aichat-text);
  white-space: nowrap;
`;

// overflow-x 를 hidden 으로 두면 안 된다. 모바일은 이 패널이 MUI SwipeableDrawer
// 안에 들어가는데, SwipeableDrawer 는 터치를 스와이프로 가로챌지 판단할 때
// overflow-x 가 hidden 인 조상을 스크롤 영역이 아니라고 보고 건너뛴다(getDomTreeShapes).
export const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 16px 180px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 8px;
  animation: ${contentIn} 0.2s ease both;
`;

export const Greeting = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

export const GreetingSub = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: var(--aichat-text-sub);
  text-align: center;
`;

export const GreetingMain = styled.p`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: var(--aichat-text);
  text-align: center;
`;

/* ── Chat messages ── */
const bubbleIn = keyframes`
  from { opacity: 0; transform: scale(0.5); }
  to   { opacity: 1; transform: scale(1); }
`;

export const ChatBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-bottom: 8px;
  animation: ${contentIn} 0.2s ease both;
`;

export const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

export const LoadMore = styled.div`
  padding: 4px 0;
  text-align: center;
  font-size: 12px;
  line-height: 18px;
  color: var(--aichat-text-sub);
`;

export const UserWrap = styled.div<{ isAnimated?: boolean }>`
  display: flex;
  justify-content: flex-end;
  transform-origin: top right;
  animation: ${({ isAnimated }) => (isAnimated ? bubbleIn : 'none')} 0.45s
    cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
`;

export const UserBubble = styled.div`
  max-width: calc(100% - 24px);
  padding: 8px 12px;
  background: var(--aichat-user-bubble);
  border-radius: 16px 16px 4px 16px;
  border: 1px solid var(--aichat-user-border);
`;

export const UserText = styled.p`
  margin: 0;
  color: var(--aichat-user-text);
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  word-break: break-word;
  white-space: pre-wrap;
`;

export const AiGroup = styled.div<{ isAnimated?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  transform-origin: top left;
  animation: ${({ isAnimated }) => (isAnimated ? bubbleIn : 'none')} 0.45s
    cubic-bezier(0.34, 1.56, 0.64, 1) 0.12s both;
`;

export const AiHead = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const AvatarSlot = styled.div`
  position: relative;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
`;

export const AiStatus = styled.span<{ isDone?: boolean }>`
  font-size: 13px;
  line-height: 20px;
  color: ${({ isDone }) =>
    isDone ? 'var(--aichat-text-sub)' : 'var(--aichat-text-muted)'};
`;

// AI 답변 본문 — 에디터 본문과 같은 기준 폰트(18px / 1.5). 구조/색은 Markdown 이 담당.
export const AiText = styled.div<{ isMobile?: boolean }>`
  margin: 0;
  font-size: 18px;
  line-height: 1.5;
  color: var(--aichat-text);
  word-break: break-word;
`;

// 답변 본문과 액션 버튼 사이 구분선
export const MsgDivider = styled.div`
  height: 1px;
  background: var(--aichat-divider);
`;

export const MsgActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding-bottom: 4px;
`;

// 답변 하단 액션 버튼(복사/내보내기/이어붙이기 등) — 각 기능이 renderActions 로 재사용.
export const ActionBtn = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: none;
  border-radius: 8px;
  background: none;
  cursor: pointer;
  color: var(--aichat-text);
  flex-shrink: 0;

  &:hover {
    background: var(--aichat-hover);
  }
  &:active {
    background: var(--aichat-active);
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

export const SourceChanged = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 2px 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--aichat-text-sub);

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--aichat-divider);
  }
`;

/* ── Footer / input ── */
export const Footer = styled.div<{ isMobile?: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 48px 16px 20px;
  background: ${({ isMobile }) =>
    isMobile
      ? 'linear-gradient(180deg, var(--aichat-footer-from-mobile) 0%, var(--aichat-footer-to-mobile) 45%)'
      : 'linear-gradient(180deg, var(--aichat-footer-from) 0%, var(--aichat-footer-to) 45%)'};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

export const Chips = styled.div`
  align-self: stretch;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const Chip = styled.button`
  padding: 8px;
  background: var(--aichat-input-bg);
  border-radius: 8px;
  border: 1px solid var(--aichat-border);
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  color: var(--aichat-text);
  cursor: pointer;
  user-select: none;
  text-align: left;

  &:hover {
    background: var(--aichat-chip-hover);
  }

  &:disabled {
    cursor: default;
    color: var(--aichat-text-muted);
  }
`;

export const InputCard = styled.div<{ isDisabled: boolean }>`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 16px var(--aichat-shadow);
  border-radius: 16px;
  ${({ isDisabled }) => isDisabled && `pointer-events: none;`}
`;

export const InputNotice = styled.p`
  padding: 10px 14px 36px;
  background: var(--aichat-input-bg);
  border-radius: 16px 16px 0 0;
  border: 1px solid var(--aichat-border);
  border-bottom: none;
  font-size: 13px;
  line-height: 20px;
  color: var(--aichat-text-sub);
  margin: 0 0 -28px;
`;

export const InputWrap = styled.div<{
  hasContent: boolean;
  isSending: boolean;
  isDisabled: boolean;
}>`
  align-self: stretch;
  padding: 16px;
  background: ${({ isDisabled }) =>
    isDisabled ? 'var(--aichat-input-bg-disabled)' : 'var(--aichat-input-bg)'};
  border-radius: 16px;
  border: 1px solid var(--aichat-border);
  box-shadow: ${({ isDisabled }) =>
    isDisabled ? 'none' : '0 0 16px var(--aichat-shadow)'};
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  z-index: 1;

  &:hover,
  &:focus-within {
    border-color: ${({ isDisabled }) =>
      isDisabled ? 'var(--aichat-border)' : 'var(--aichat-input-focus)'};
  }
`;

// iOS 는 포커스 대상의 font-size 가 16px 미만이면 화면을 자동 확대한다(PORTAL-13369).
// MO 만 16px/24px 로 올려 확대를 막고, PC 는 기존 디자인(14px/20px)을 유지한다.
export const Input = styled.textarea<{ disabled: boolean; isMobile?: boolean }>`
  flex: 1;
  min-width: 0;
  max-height: 100px;
  min-height: ${({ isMobile }) => (isMobile ? '24px' : '20px')};
  resize: none;
  overflow-y: auto;
  outline: none;
  border: none;
  background: transparent;
  font-size: ${({ isMobile }) => (isMobile ? '16px' : '14px')};
  font-weight: 400;
  line-height: ${({ isMobile }) => (isMobile ? '24px' : '20px')};
  color: ${({ disabled }) =>
    disabled ? 'var(--aichat-input-disabled-text)' : 'var(--aichat-text)'};
  word-break: break-word;
  scrollbar-width: none;
  font-family: inherit;
  padding: 0;

  &::-webkit-scrollbar {
    display: none;
  }

  &::placeholder {
    color: ${({ disabled }) =>
      disabled
        ? 'var(--aichat-input-disabled-text)'
        : 'var(--aichat-text-muted)'};
  }
`;

// 입력창과 같은 줄에 놓이는 전송 버튼 (32px, PC/MO 동일)
export const Send = styled.button<{ active: boolean; isMobile?: boolean }>`
  align-self: flex-end;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: ${({ active }) =>
    active ? 'var(--aichat-send-active)' : 'var(--aichat-send-inactive)'};
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: ${({ active }) => (active ? 'pointer' : 'default')};
  transition: background 0.15s, transform 0.1s;

  &:active {
    ${({ active }) =>
      active &&
      `background: var(--aichat-send-pressed); transform: scale(0.875);`}
  }
`;

export const StopSquare = styled.div<{ isMobile?: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '12px' : '10px')};
  height: ${({ isMobile }) => (isMobile ? '12px' : '10px')};
  aspect-ratio: 1 / 1;
  flex-shrink: 0;
  background: var(--dk-bg, #fff);
  border-radius: 2px;
`;

export const Disclaimer = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--aichat-text-sub);
  text-align: center;
`;
